/**
 * The event mapping of a legacy MCE adapter: a file path on the VTS host and the
 * events XML itself. Both are plain text on the adapter's stored Mapping, and VTS reads
 * the file first and the XML when there is no readable file.
 */
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Input, Typography } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload } from "@fortawesome/free-solid-svg-icons";
import FormInputText from "../../../components/form/FormInputText";
import { FormularyType } from "../../../../interfaces/Wizard.interface";
import pin from "../../../../wasm/mce_idl/pin.json";
import { EventAddressStatus } from "../../../../utils/mceIdl/eventAddressStatus";
import {
  EventMappingFinding,
  checkEventMapping,
  describeFinding,
} from "../../../../utils/mceIdl/eventMappingCheck";
import { decodeEventsFile } from "../../../../utils/mceIdl/eventMappingEdit";

const KIB = 1024;
const DATA_ROWS = 14;
const MONOSPACE = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
/**
 * The check's result has a line of its own that is always there, one line high. A result
 * that arrived when the box lost focus used to push the Update button down between the
 * press and the release of a click on it, and the click was lost.
 */
const STATUS_LINE: React.CSSProperties = {
  minHeight: 22,
  lineHeight: "22px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

type Check =
  | { phase: "idle" }
  | { phase: "checking" }
  | { phase: "done"; findings: EventMappingFinding[] }
  | { phase: "unavailable" };

export type MceEventField = "mapping_file" | "mapping_data";

interface MceLegacyEventFieldsProps {
  mappingFile: string;
  mappingData: string;
  onChange: (field: MceEventField, value: string) => void;
  addressStatus: EventAddressStatus;
}

export default function MceLegacyEventFields({
  mappingFile,
  mappingData,
  onChange,
  addressStatus,
}: MceLegacyEventFieldsProps) {
  const [t] = useTranslation("global");
  const [check, setCheck] = useState<Check>({ phase: "idle" });
  const [fileProblem, setFileProblem] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const latestCheck = useRef(0);

  const runCheck = async (xml: string) => {
    latestCheck.current += 1;
    const mine = latestCheck.current;
    if (xml.trim() === "") {
      setCheck({ phase: "idle" });
      return;
    }
    setCheck({ phase: "checking" });
    try {
      const findings = await checkEventMapping(xml);
      if (mine === latestCheck.current) setCheck({ phase: "done", findings });
    } catch {
      if (mine === latestCheck.current) setCheck({ phase: "unavailable" });
    }
  };

  const changeData = (value: string) => {
    // A result about older text says nothing about this text.
    latestCheck.current += 1;
    setCheck({ phase: "idle" });
    onChange("mapping_data", value);
  };

  const loadFile = async (file: File) => {
    const max = pin.maxEventsBytes;
    if (file.size > max) {
      setFileProblem(t("general.eventMappingFileTooLarge", { name: file.name, max: max / KIB }));
      return;
    }
    setFileProblem(null);
    const text = decodeEventsFile(new Uint8Array(await file.arrayBuffer()));
    onChange("mapping_data", text);
    runCheck(text);
  };

  const onPicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Picking the same file again must load it again.
    const input = event.target;
    if (file) loadFile(file);
    input.value = "";
  };

  const checkLine = (): React.ReactNode => {
    if (check.phase === "checking") {
      return (
        <Typography.Text type="secondary">{t("general.eventMappingChecking")}</Typography.Text>
      );
    }
    if (check.phase === "unavailable") {
      return (
        <Typography.Text type="warning">{t("general.eventMappingNotChecked")}</Typography.Text>
      );
    }
    if (check.phase !== "done") return null;
    if (check.findings.length === 0) {
      return <Typography.Text type="success">{t("general.eventMappingChecked")}</Typography.Text>;
    }
    const [first, ...rest] = check.findings;
    const more =
      rest.length > 0 ? ` (${t("general.eventMappingMore", { count: rest.length })})` : "";
    return (
      <Typography.Text
        type="danger"
        data-testid="mce-events-finding"
        title={`${describeFinding(first)}${more}`}
      >
        {`${describeFinding(first)}${more}`}
      </Typography.Text>
    );
  };

  const bothEmpty = mappingFile.trim() === "" && mappingData.trim() === "";

  return (
    <div data-testid="mce-legacy-event-fields">
      <div className="row">
        <div className="col-12 col-md-6 mt-3">
          <FormInputText
            inputValue={mappingFile}
            inputLabelName={t("general.eventMappingFile")}
            inputOnChange={(value) => onChange("mapping_file", value)}
            inputVariableName="mapping_file"
            isRequired={false}
            disabledInput={false}
            formularyType={FormularyType.WIZARD}
            placeholder="/opt/mce/Events.xml"
          />
          <Typography.Paragraph type="secondary" className="mt-1 mb-0">
            {t("general.eventMappingFileHelp")}
          </Typography.Paragraph>
        </div>
      </div>
      <div className="row">
        <div className="col-12 mt-3">
          <div
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
            className="generalStyles__inputFlexNameLarge"
          >
            <span className="drawer__text" style={{ marginLeft: 8 }}>
              {t("general.eventMappingData")}
            </span>
            <Button
              size="small"
              icon={<FontAwesomeIcon icon={faUpload} className="me-2" />}
              onClick={() => picker.current?.click()}
            >
              {t("general.eventMappingLoadXml")}
            </Button>
            <input
              ref={picker}
              type="file"
              accept=".xml,text/xml,application/xml"
              style={{ display: "none" }}
              aria-label={t("general.eventMappingLoadXml")}
              data-testid="mce-events-file-input"
              onChange={onPicked}
            />
          </div>
          <Input.TextArea
            className="mt-2"
            aria-label={t("general.eventMappingData")}
            value={mappingData}
            rows={DATA_ROWS}
            wrap="off"
            spellCheck={false}
            onChange={(event) => changeData(event.target.value)}
            onBlur={() => runCheck(mappingData)}
            style={{ fontFamily: MONOSPACE, fontSize: 12 }}
          />
          <div className="mt-1" aria-live="polite" style={STATUS_LINE}>
            {checkLine()}
            {fileProblem && <Typography.Text type="danger">{fileProblem}</Typography.Text>}
          </div>
          <Typography.Paragraph type="secondary" className="mt-1 mb-0">
            {t("general.eventMappingDataHelp")}
          </Typography.Paragraph>
          {bothEmpty && (
            <Typography.Paragraph type="warning" className="mt-1 mb-0">
              {t("general.eventMappingBothEmpty")}
            </Typography.Paragraph>
          )}
          <Typography.Paragraph type="secondary" className="mt-1 mb-0">
            {t("general.eventMappingFaultsNeedAddress")}
          </Typography.Paragraph>
          {addressStatus.state === "missing" && (
            <Alert
              className="mt-2"
              type="warning"
              showIcon
              message={t("general.eventMappingBankNoAddress", {
                names: addressStatus.banks.join(", "),
              })}
            />
          )}
        </div>
      </div>
    </div>
  );
}
